// @flow
import * as React from 'react';
import { useEffect, useRef } from 'react';
import classNames from 'classnames';
import Icon from 'components/_particles/icon/react/Icon';
import type { PictureType } from 'components/atoms/picture/react/Picture';
import Headline from 'components/atoms/headline/react/Headline';
import IconLink from 'components/atoms/icon-link/react/IconLink';
import addOverlayLink from 'javascripts/utils/overlay-link';
import Figure from 'components/molecules/figure/react/Figure';
import MediaButton from 'components/atoms/media-button/react/MediaButton';
import type { MediaButtonType } from 'components/atoms/media-button/react/MediaButton';

export type TeaserType = {
  horizontal?: boolean,
  small?: boolean,
  attributes?: any,
  image: PictureType,
  date?: {
    date: string,
    text: string,
  },
  mediaType?: MediaButtonType,
  meta?: Array<{ icon?: string, text: string }>,
  headline: string,
  text?: string,
  hashtags?: Array<{ link: string, text: string }>,
  moreLink?: {
    link: string,
    text: string,
  },
  publication?: boolean,
  className?: string,
  copyright?: string,
  border?: boolean,
  subtle?: boolean,
};

const Teaser = ({
  image,
  horizontal,
  small,
  attributes,
  date,
  mediaType,
  meta,
  headline,
  text,
  hashtags,
  moreLink,
  publication,
  className,
  copyright,
  border,
  subtle,
}: TeaserType): React.MixedElement => {
  const overlay = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    addOverlayLink(overlay.current);
  }, []);

  return (
    <article
      className={classNames(
        {
          teaser: true,
          'teaser--horizontal': horizontal,
          'teaser--small': small,
          'teaser--border': border,
          'teaser--subtle': subtle,
        },
        className
      )}
      {...attributes}
    >
      {(image || date || (!image && mediaType)) && (
        <div
          className={`teaser__image
              ${
                (!image || (image && !image.src)) && mediaType
                  ? 'teaser__image--placeholder'
                  : ''
              }`}
        >
          {mediaType && (
            <div className="teaser__image-media">
              {image.src ? (
                <Figure picture={image} mediaType={mediaType} />
              ) : (
                <div className="figure__image-media">
                  <MediaButton
                    {...mediaType}
                    className="figure__image-button"
                  />
                </div>
              )}
            </div>
          )}

          {publication && (
            <div className="teaser__image-publication">
              <div className="teaser__image-cover">
                <Figure picture={image} />
              </div>
            </div>
          )}

          {!mediaType && !publication && image && <Figure picture={image} />}

          {copyright && (
            <div className="figure__copyright">
              <div className="copyright">
                <Icon icon="camera" className="copyright__icon" />
                <span className="copyright__text">{copyright}</span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="teaser__content">
        <div className="teaser__content-inner">
          <div className="teaser__header">
            {meta && (
              <ul className="teaser__meta">
                {meta.map((item, index) => (
                  <li className="teaser__meta-item" key={index}>
                    {item.icon && (
                      <Icon icon={item.icon} className="teaser__meta-icon" />
                    )}
                    <span className="teaser__meta-text">{item.text}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="teaser__headline">
              <Headline size={3} level={3}>
                {headline}
              </Headline>
            </div>
          </div>

          {text && (
            <div
              dangerouslySetInnerHTML={{ __html: text }}
              className="teaser__text"
            ></div>
          )}

          <div className="teaser__footer">
            {hashtags && (
              <ul className="teaser__hashtags">
                {hashtags.map((tag, index) => (
                  <li key={index} className="teaser__hashtag">
                    <a className="teaser__hashtag-link" href={tag.link}>
                      {tag.text}
                    </a>
                  </li>
                ))}
              </ul>
            )}
            {moreLink && (
              <IconLink
                icon="arrow-right-line"
                link={moreLink.link}
                className="teaser__overlay-link"
                ref={overlay}
                overlayTarget="teaser"
              >
                {moreLink.text}
              </IconLink>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default Teaser;
